import React, { useEffect, useMemo, useState } from 'react';
import Spinner from 'components/spinner/Spinner';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import MetaTag from 'components/meta';
import dayjs from 'dayjs';
import { PatientPharmacyAllergyMedicationRequestDTO } from 'components/pharmacyMedications/types/types';
import Button from 'components/common/button';
import styles from 'components/medicalHistory/page.module.scss';
import Checkbox from '@mui/material/Checkbox';
import { appStateSettingsAction } from 'reduxStateSlice/appState';
import { ModuleTypes } from 'types/appState';
import { parseErrorMessage } from 'utils/helper';
import axios from 'axios';
import { NullablePatientDTO } from 'types/patients';
import isNil from 'lodash/isNil';
import Alert, { AlertVariant } from 'components/common/Alerts';
import AlertTitle from '@mui/material/AlertTitle';
import { DosespotConsentByPatientDTO } from 'artifacts/dtos';
import {
  useAddPatientPharmacyAllergyAndMedication,
  useSubmitDosespotConsentForm,
} from 'hooks/useApi';
import useCommuniqueState from 'hooks/useCommuniqueState';
import { appointmentIntakeStatusAction } from 'reduxStateSlice/appointment';

const title = 'Patient Consent';
type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

export default function PatientConsent({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const { setCommuniqueData, communiqueData } = useCommuniqueState();
  const reduxDispatch = useDispatch();
  const [consent, setConsent] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [signatureError, setSignatureError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const {
    mutateAsync: submitDosespotConsentForm,
    isPending: isLoading,
    isSuccess,
    error,
  } = useSubmitDosespotConsentForm();
  const {
    mutateAsync: addPatientPharmacyAllergyAndMedication,
  } = useAddPatientPharmacyAllergyAndMedication();

  const patient = useSelector(
    (state: RootState) => state.patient,
  ) as NullablePatientDTO;

  const onSubmit = async () => {
    if (!consent) {
      setSignatureError(true);
    } else {
      setErrorMessage('');
      const payload: DosespotConsentByPatientDTO = {
        patientId: communiqueData?.patientId ?? 0,
        consentGiven: consent,
        consentGivenAt: dayjs().format(),
      };
      try {
        await submitDosespotConsentForm(payload);
      } catch (e) {
        setErrorMessage(parseErrorMessage(e));
      }
    }
  };

  const skipOnSubmit = async () => {
    setIsSubmitting(true);
    const payload: PatientPharmacyAllergyMedicationRequestDTO = {
      patientId: communiqueData?.patientId ?? 0,
      appointmentId: communiqueData?.appointmentId ?? 0,
    };

    try {
      await addPatientPharmacyAllergyAndMedication(payload);
      if (!isNil(communiqueData)) {
        setCommuniqueData({
          ...communiqueData,
          sendForms: {
            ...communiqueData?.sendForms,
            pharmacyAllergyMedicationsSubmitted: false,
          },
        });
      }
      reduxDispatch(appointmentIntakeStatusAction({
        pharmacyAllergyMedicationsSubmitted: true,
      }));
      const module = communiqueData?.sendForms?.consentForm
        ? ModuleTypes.consentForm : ModuleTypes.completed;
      reduxDispatch(
        appStateSettingsAction({
          name: module,
          page: null,
        }),
      );
      setIsSubmitting(false);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        setErrorMessage(`${parseErrorMessage(error)}`);
      } else {
        setErrorMessage('Something went wrong on AddPatientPharmacyAllergyAndMedication');
        console.error(
          '🚀 ~ error on Submitting AddPatientPharmacyAllergyAndMedication : ',
          error,
        );
      }
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess) {
      nextOrSubmitButton(nextPage);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (!isNil(error)) {
      setErrorMessage(parseErrorMessage(error));
    }
  }, [error]);

  const onChange = (e: unknown, checked: boolean) => {
    setConsent(checked);
    setSignatureError(false);
  };

  const hasDosespotError = useMemo(() => !isNil(patient?.dosespotError), [patient]);

  return (
    <>
      <MetaTag title={title} />
      <div>
        <h2 className={`marginTopZero text-center ${styles.titleText}`}>Patient Consent</h2>
        <div className="flex my-2">
          <Checkbox
            checked={consent}
            onChange={(e, checked) => {
              onChange(e, checked);
            }}
            size="small"
            className="!-mt-6"
          />
          <p>I consent to sharing my pharmacy, medication, and allergy information.</p>
        </div>
        <div>
          {errorMessage && (
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <p className="error-message">{errorMessage}</p>
              </div>
            </div>
          )}
          {hasDosespotError && (
            <Alert
              severity={AlertVariant.ERROR}
              open
              className="mb-3 !w-full"
              hasIcon={false}
            >
              <AlertTitle
                className="!text-sm"
              >
                {patient?.dosespotError?.title ?? ''}
              </AlertTitle>
              <p className="text-xs">
                {patient?.dosespotError?.details ?? ''}
              </p>
            </Alert>
          )}
          {signatureError && (
          <p className="error-message">
            Consent is not given.
          </p>
          )}
        </div>
        <div className="my-4">

          {isLoading || isSubmitting ? (
            <div className="flex items-center justify-center">
              <div className="flex items-center">
                <Spinner />
              </div>
            </div>
          )
            : (
              <div className="flex flex-col gap-2">
                <Button
                  disabled={isLoading || hasDosespotError}
                  onClick={onSubmit}
                >
                  Submit
                </Button>
                <Button
                  disabled={isLoading}
                  onClick={skipOnSubmit}
                  variant="outlined"
                >
                  Skip
                </Button>
              </div>
            )}
        </div>
      </div>
    </>
  );
}
